import { createStore } from './state/BuilderStore';
import { BuilderFlow } from './flow/BuilderFlow';
import { loadBootstrapConfig } from './api/client';
import { parseSettings } from './config/BuilderConfig';
import type { ThemeColors } from './config/BuilderConfig';
import type { StepId } from './state/types';

/**
 * Inject per-tenant theme colors as CSS custom properties on the container
 * element. Scoped to the element so they override :root defaults without
 * affecting any other content on the host page.
 */
function applyTheme(el: HTMLElement, theme: ThemeColors): void {
  el.style.setProperty('--vc-primary',       theme.primary);
  el.style.setProperty('--vc-primary-hover',  theme.primaryHover);
  el.style.setProperty('--vc-primary-light',  theme.primaryLight);
  // Border uses primary at 33% opacity as a derived semi-transparent tint,
  // but if the admin set an explicit border color we use that opaque value.
  el.style.setProperty('--vc-border',         theme.border + '55');
  el.style.setProperty('--vc-text',           theme.text);
  el.style.setProperty('--vc-bg',             theme.bg);
}

/**
 * Main entry point for the Vehicle Customizer builder.
 */
async function init(): Promise<void> {
  const container = document.getElementById('vehicle-customizer');
  if (!container) {
    console.error('Vehicle Customizer: #vehicle-customizer container not found');
    return;
  }

  try {
    // Resolve tenant ID from URL param → localStorage (must happen before bootstrap fetch)
    const initParams = new URLSearchParams(window.location.search);
    const urlTenantId = initParams.get('tenant');
    if (urlTenantId) {
      localStorage.setItem('vc_tenant_id', urlTenantId);
    }

    // Local dev fallback: seed tenant ID from VITE_TENANT_ID env var when no
    // other source (URL param, localStorage) has already set one. This lets
    // developers point the builder at a specific tenant without touching the DB.
    // Set VITE_TENANT_ID=<id> in ui/.env.local (git-ignored).
    const devTenantId = import.meta.env.VITE_TENANT_ID as string | undefined;
    if (devTenantId && !urlTenantId && !localStorage.getItem('vc_tenant_id')) {
      localStorage.setItem('vc_tenant_id', devTenantId);
    }

    // Load configuration from API
    const config = await loadBootstrapConfig();

    // Cache the server-confirmed tenant ID so all subsequent API requests in
    // this session send X-Tenant-Id rather than relying on Origin header each time.
    if (config.tenant?.id) {
      localStorage.setItem('vc_tenant_id', String(config.tenant.id));
    }

    const settings = parseSettings(config);

    // Apply per-tenant theme — inject CSS custom properties directly onto the
    // container element so they override the :root defaults for this widget only.
    // This works for both the standalone page and the embedded script tag.
    applyTheme(container, settings.theme);

    // Determine initial model and step from config / URL
    let initialModelId: number | null = settings.directModelId;
    let initialStep: StepId = 'collection';

    // Check for model ID in URL params (e.g., ?model=5)
    const urlParams = new URLSearchParams(window.location.search);
    const urlModelId = urlParams.get('model');
    if (urlModelId) {
      initialModelId = parseInt(urlModelId, 10);
    }

    // If a direct model ID is set, skip collection/model-select and go to color
    if (initialModelId) {
      initialStep = 'boat-color';
    } else if (settings.enableCollectionStep === -1) {
      initialStep = 'model-select';
    }

    // Check for dealer token in URL or localStorage
    const urlToken = urlParams.get('token');
    const savedToken = localStorage.getItem('vc_token');
    const dealerToken = urlToken || savedToken || null;
    const isDealer = !!dealerToken;
    if (urlToken) {
      localStorage.setItem('vc_token', urlToken);
    }

    // Initialize state store
    const store = createStore({
      selectedModelId: initialModelId,
      selectedColors: {},
      selectedEngineId: null,
      selectedOptions: [],
      selectedDealerId: null,
      quoteData: {},
      quoteTotals: null,
      currentStep: initialStep,
      isDealer,
      dealerToken,
    });

    // Initialize and start the builder flow
    const flow = new BuilderFlow(container, store, config);
    flow.start();

    console.log('Vehicle Customizer initialized successfully');
  } catch (error) {
    console.error('Vehicle Customizer initialization failed:', error);
    container.innerHTML = `
      <div class="vc-error">
        <h2>Unable to load the vehicle customizer</h2>
        <p>Please try refreshing the page. If the problem persists, contact support.</p>
      </div>
    `;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
